
import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";

interface CopySuppliesListProps {
  supplies: Array<{
    id: string;
    name: string;
    checked: boolean;
  }>;
  checkedOnly?: boolean;
}

const CopySuppliesList: React.FC<CopySuppliesListProps> = ({ 
  supplies, 
  checkedOnly = false 
}) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    const filteredSupplies = checkedOnly 
      ? supplies.filter(supply => supply.checked)
      : supplies;

    const supplyNames = filteredSupplies
      .map(supply => supply.name)
      .join(', ');

    if (supplyNames) {
      navigator.clipboard.writeText(supplyNames)
        .then(() => {
          setCopied(true);
          toast({
            title: 'Copied to clipboard!',
            description: `${filteredSupplies.length} supplies copied as a comma-separated list.`,
          });
          
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Error copying to clipboard:', err);
          toast({
            title: 'Failed to copy',
            description: 'Please try again or copy manually.',
            variant: 'destructive',
          });
        });
    } else {
      toast({
        title: 'Nothing to copy',
        description: 'There are no supplies to copy.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={copyToClipboard}
      className="flex items-center gap-1"
    >
      {copied ? (
        <>
          <Check className="h-4 w-4" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" />
          Copy {checkedOnly ? 'Selected' : 'All'} Supplies
        </>
      )}
    </Button>
  );
};

export default CopySuppliesList;
